import { addDays, startOfWeek } from "date-fns";
import React, { lazy } from "react";
import { PastFuture } from "actions/context";
import { WORKFORCE } from "app_constants";
import type { IDatePickerProps } from "components/chrome/main_view/date_picker/DatePicker";
import { CalendarIcon } from "components/icons/CalendarIcon";
import {
  AbsoluteDateRange,
  DateRangeSupport,
  QueryParamDateFormat,
  formatForQueryParams,
} from "helpers/shared/formatters/dates";
import type { IEntity } from "interfaces/app";
import { workforcePermission } from "navigation/permissions";

import { LD_FEATURE_FLAGS } from "types/context";
import type { ISectionProps } from "types/navigation";

const WORKFORCE_DATEPICKER_PROPS: Partial<IDatePickerProps> = {
  // It was when workforce was implemented.
  yearStart: 2019,
  yearOrder: "desc",
};

const WORKFORCE_SECTION: ISectionProps = {
  feature: "Workforce",
  icon: <CalendarIcon />,
  path: "/workforce",
  level: ["store"],
  dateSupport: DateRangeSupport.NONE,
  component: lazy(() => import("containers/chrome/schedule_teaser_container")),
  requiresFeatureFlag: LD_FEATURE_FLAGS.workforceScheduleTeaser,
  subSections: [
    {
      feature: "Weekly Schedule",
      app: WORKFORCE,
      dateSupport: DateRangeSupport.FUTURE_BY_WEEK,
      requiresFeatureFlag: LD_FEATURE_FLAGS.workforceScheduleAndSettings,
      hideTitleInToolbar: true,
      level: ["store"],
      scope: ["staff", "superuser"],
      selfRouting: true,
      path: "/workforce/schedule",
      verifyPermissions: workforcePermission,
      legacyPath: ({ startDate, endDate }) => {
        return `/schedule?startDate=${formatForQueryParams(
          startDate,
          QueryParamDateFormat.UPSERVE
        )}&endDate=${formatForQueryParams(
          endDate,
          QueryParamDateFormat.UPSERVE
        )}`;
      },
      dateRangeHandler: (
        selectedStore: IEntity | undefined,
        startOfWeekOffset: number
      ) => {
        const currentTradingDay = getTradingDay(selectedStore?.tradingDay);
        const weekStart = startOfWeek(currentTradingDay, {
          weekStartsOn: startOfWeekOffset,
        });
        return {
          pastOrFuture: PastFuture.FUTURE,
          dateRange: {
            start: weekStart,
            end: addDays(weekStart, 6),
            rangeKey: AbsoluteDateRange.WEEK,
          },
        };
      },
      datePickerProps: WORKFORCE_DATEPICKER_PROPS,
    },
    {
      feature: "Daily Schedule",
      app: WORKFORCE,
      dateSupport: DateRangeSupport.FUTURE_BY_WEEK,
      requiresFeatureFlag: LD_FEATURE_FLAGS.workforceDayView,
      hideTitleInToolbar: true,
      level: ["store"],
      path: "/workforce/schedule_day",
      scope: ["staff", "superuser"],
      selfRouting: true,
      verifyPermissions: workforcePermission,
      legacyPath: ({ startDate }) => {
        return `/schedule?startDate=${formatForQueryParams(
          startDate,
          QueryParamDateFormat.UPSERVE
        )}&dayView=${formatForQueryParams(
          startDate,
          QueryParamDateFormat.UPSERVE
        )}`;
      },
      dateRangeHandler: (selectedStore: IEntity | undefined) => {
        const currentTradingDay = getTradingDay(selectedStore?.tradingDay);
        return {
          pastOrFuture: PastFuture.FUTURE,
          dateRange: {
            start: currentTradingDay,
            end: currentTradingDay,
            rangeKey: AbsoluteDateRange.WEEK,
          },
        };
      },
      datePickerProps: WORKFORCE_DATEPICKER_PROPS,
    },
    {
      feature: "Settings",
      app: WORKFORCE,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/workforce/settings",
      legacyPath: () => {
        return "/settings";
      },
      dateSupport: DateRangeSupport.NONE,
      requiresFeatureFlag: LD_FEATURE_FLAGS.workforceScheduleAndSettings,
      verifyPermissions: workforcePermission,
    },
  ],
};

export default WORKFORCE_SECTION;

function getTradingDay(tradingDayString: string | undefined): Date {
  if (!tradingDayString) {
    return new Date();
  }
  // The T00:00 WITHOUT Z makes sure it is calculated on local time not Greenwich time
  const tradingDay = new Date(tradingDayString + "T00:00");
  if (isNaN(tradingDay.getTime())) {
    return new Date();
  }
  return tradingDay;
}
